<template>
    <b-card v-if="shouldRender" :class="[cardClass, 'mx-auto text-center py-4 border-warning shadow-sm missing-source-card']" style="max-width: 600px;">
        <div class="d-flex flex-column align-items-center px-4">
            <i :class="[computedIcon, 'text-warning mb-3']" style="font-size: 3rem;"></i>
            <h4 class="font-weight-bold mb-3">{{ computedTitle }}</h4>
            <p class="text-muted mb-4" style="max-width: 480px;">
                <slot>
                    <span v-if="isTeam">
                        No Team Space is configured yet. Team Spaces are provisioned on demand from your 5-seat batch pool.
                    </span>
                    <span v-else-if="activeProjectName">
                        The active project <strong>"{{ activeProjectName }}"</strong> does not have a {{ isRepo ? 'repository' : 'backlog' }} source connected.
                    </span>
                    <span v-else-if="!hasConnectedPlatform">
                        No platform connection is configured yet. Connect Azure DevOps, GitHub, or GitLab to start using VelocyTrack.
                    </span>
                    <span v-else-if="!hasConfiguredProject">
                        No project is configured yet. Set up a project and link your {{ isRepo ? 'repository' : 'backlog' }} to start using VelocyTrack.
                    </span>
                    <span v-else>
                        No {{ isRepo ? 'repository' : 'backlog' }} source is connected to this project.
                    </span>
                    <span class="d-block mt-2">{{ computedDetailText }}</span>
                </slot>
            </p>
            <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                <b-button variant="primary" :to="configRoute" @click="onConfigureClick" data-test="btn-configure-source">
                    <i class="bi bi-gear-fill mr-1"></i> {{ computedButtonText }}
                </b-button>
                <b-dropdown
                    v-if="!isTeam && configuredProjects.length > 1"
                    variant="outline-secondary"
                    size="sm"
                    class="ml-2"
                    data-test="dropdown-switch-project-missing"
                >
                    <template #button-content>
                        <i class="bi bi-folder2-open mr-1"></i> Switch Project Space
                    </template>
                    <b-dropdown-header class="small font-weight-bold">Select Active Project Space</b-dropdown-header>
                    <b-dropdown-item-button
                        v-for="p in configuredProjects"
                        :key="p.id"
                        :active="p.id === activeProjectId"
                        @click="onSwitchProject(p.id)"
                        class="missing-source-project-item"
                    >
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <div class="d-flex align-items-center text-truncate mr-2">
                                <i v-if="p.id === activeProjectId" class="bi bi-check2 text-success mr-2 font-weight-bold"></i>
                                <span v-else style="display: inline-block; width: 1rem;" class="mr-2"></span>
                                <span class="text-truncate">{{ p.projectSpaceName || p.projectName }}</span>
                            </div>
                            <div class="missing-platform-logos d-flex align-items-center flex-shrink-0">
                                <i v-for="plat in getProjectPlatforms(p)"
                                   :key="plat.type"
                                   :class="[plat.icon, 'platform-logo-icon', plat.brandClass]"
                                   :title="plat.name"
                                   class="ml-1"></i>
                            </div>
                        </div>
                    </b-dropdown-item-button>
                </b-dropdown>
                <slot name="extra-actions"></slot>
            </div>
        </div>
    </b-card>
</template>

<script>
    import eventBus from "../event-bus";
    import accountMixin from "../mixinAccount";

    export default {
        name: "CardMissingSource",
        mixins: [accountMixin],
        props: {
            sourceType: {
                type: String,
                default: "backlog",
                validator: (val) => ["backlog", "repo", "team"].includes(val)
            },
            title: {
                type: String,
                default: ""
            },
            featureName: {
                type: String,
                default: ""
            },
            description: {
                type: String,
                default: ""
            },
            project: {
                type: Object,
                default: null
            },
            icon: {
                type: String,
                default: ""
            },
            buttonText: {
                type: String,
                default: ""
            },
            cardClass: {
                type: String,
                default: "my-5"
            }
        },
        computed: {
            shouldRender() {
                if (!this.isAuthenticated) return false;
                return true;
            },
            isRepo() {
                return this.sourceType === "repo";
            },
            isTeam() {
                return this.sourceType === "team";
            },
            activeProject() {
                if (this.project) {
                    return this.project;
                }
                return this.globalProject;
            },
            activeProjectId() {
                return this.activeProject ? this.activeProject.id : null;
            },
            activeProjectName() {
                return this.activeProject ? this.activeProject.projectName : "";
            },
            computedIcon() {
                if (this.icon) return this.icon;
                if (this.isTeam) return "bi bi-people-fill";
                if (!this.hasConnectedPlatform) return "bi bi-link-45deg";
                if (!this.hasConfiguredProject) return "bi bi-folder-plus";
                return this.isRepo ? "bi bi-git" : "bi bi-card-checklist";
            },
            computedTitle() {
                if (this.title) return this.title;
                if (this.isTeam) {
                    return "Team Space Setup Required";
                }
                if (!this.hasConnectedPlatform) {
                    return "Platform Connection Required";
                }
                if (!this.hasConfiguredProject) {
                    return "Project Setup Required";
                }
                if (this.featureName) {
                    return `${this.featureName} Requires ${this.isRepo ? "Repository" : "Backlog"} Setup`;
                }
                return `No ${this.isRepo ? "Repository" : "Backlog"} Source Linked`;
            },
            computedDetailText() {
                if (this.description) {
                    return this.description;
                }
                if (this.isTeam) {
                    return "Create a Team Space in Configuration, link shared Project Spaces, and invite squad members to track capacity, sprint deliverables, and leave schedules.";
                }
                if (!this.hasConnectedPlatform) {
                    return "Connect your platform account to view work items, track hours, and manage pull requests.";
                }
                if (!this.hasConfiguredProject) {
                    return "Create a project and link it to your repositories or boards to start tracking.";
                }
                if (this.featureName === "Timelog" || this.featureName === "Time Manager") {
                    return "Timelog tracks hours against user stories and tasks in sprint backlogs.";
                }
                if (this.featureName === "Backlog" || this.featureName === "Task Manager") {
                    return "Backlog requires a connected backlog (such as Azure DevOps or Jira) to track user stories and sprint tasks.";
                }
                if (this.featureName === "Pull Requests" || this.featureName === "PR Manager") {
                    return "Pull Requests requires a connected Git repository to track pull requests.";
                }
                if (this.isRepo) {
                    return "A connected Git repository is required to view and track pull requests.";
                }
                return "A connected backlog (such as Azure DevOps or Jira) is required to track user stories and sprint tasks.";
            },
            computedButtonText() {
                if (this.buttonText) return this.buttonText;
                if (this.isTeam) {
                    return "Configure Team Space in Configuration";
                }
                if (!this.hasConnectedPlatform) {
                    return "Connect Platform in Configuration";
                }
                if (!this.hasConfiguredProject) {
                    return "Create Project in Configuration";
                }
                return `Configure ${this.isRepo ? "Repository" : "Backlog"} in Project Settings`;
            },
            configRoute() {
                if (this.isTeam) {
                    return {
                        path: "/config",
                        query: {
                            section: "teams"
                        }
                    };
                }
                if (!this.hasConnectedPlatform) {
                    return {
                        path: "/config",
                        query: {
                            section: "connections"
                        }
                    };
                }
                return {
                    path: "/config",
                    query: {
                        section: "projects",
                        editProject: this.activeProjectId
                    }
                };
            }
        },
        methods: {
            onConfigureClick(e) {
                this.$emit("configure-click", e);
            },
            onSwitchProject(newProjId) {
                if (newProjId === this.activeProjectId) return;
                this.$store.commit('setSelectedProjectId', newProjId);
                try {
                    localStorage.setItem('velocytrack_last_project_id', newProjId);
                } catch (e) {
                    // ignore
                }
                eventBus.$emit('global-project-changed', newProjId);
            }
        }
    };
</script>

<style scoped>
.missing-source-card {
    border-color: #f59e0b !important;
}
</style>
